import { useServerInfo } from '@go-client/context/ServerInfoContext';
import {
  evaluateMcmEndpoint,
  evaluateMcmTruthyAggregate,
  mcmEndpointToName,
  toggleMcmEndpoint,
  toggleMcmSync,
} from '@go-shared/lib/mcm';
import { Mcm } from '@go-shared/types/mcm';
import React from 'react';
import { BubbleButton, BubbleCheckbox, BubbleTable } from './BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from './BubbleUI/BubbleTable';

interface VisibilityTableItem {
  key: string;
  label: string;
  visible: boolean | null;
  trending: boolean | null;
}

interface McmVisibleTableProps {
  isTemplate: boolean;
  readOnly?: boolean;
  sourceMcm: Mcm | undefined;
  parentMcm: Mcm | undefined;
  onUpdateMcm?: (newMcm: Mcm) => void;
}

export default function McmVisibilityTable(props: McmVisibleTableProps) {
  const { mcmEndpoints } = useServerInfo();
  const { isTemplate, sourceMcm, parentMcm, onUpdateMcm } = props;

  const visibilityTableItems: VisibilityTableItem[] = [
    {
      key: 'all',
      label: 'All',
      visible: evaluateMcmTruthyAggregate('visible', mcmEndpoints, sourceMcm, parentMcm),
      trending: evaluateMcmTruthyAggregate('trending', mcmEndpoints, sourceMcm, parentMcm),
    },
    ...mcmEndpoints.map(edp => ({
      key: edp,
      label: mcmEndpointToName(edp),
      visible: evaluateMcmEndpoint('visible', edp, sourceMcm, parentMcm),
      trending: evaluateMcmEndpoint('trending', edp, sourceMcm, parentMcm),
    })),
  ];
  const visibilityTableFields: BubbleTableFields<VisibilityTableItem>[] = [
    { key: 'actions', behavior: BubbleTableFieldBehavior.Condense },
    { key: 'key' },
  ];
  const visibilityTableTemplates: BubbleTableTemplate<VisibilityTableItem>[] = [
    {
      key: 'key',
      render(item) {
        return <div>{item.label}</div>;
      },
    },
    {
      key: 'actions',
      render(item) {
        return (
          <div className="flex items-center space-x-1">
            <BubbleButton
              variant={item.visible ? 'navy' : item.visible === null ? 'banana' : 'smoke'}
              inverse
              className="p-1-i"
              onClick={() => {
                if (onUpdateMcm) {
                  onUpdateMcm(
                    toggleMcmEndpoint('visible', item.key, mcmEndpoints, sourceMcm, parentMcm)
                  );
                }
              }}
              icon={item.visible === true ? 'eye' : item.visible === false ? 'eye-slash' : 'eye'}
            />
            <BubbleButton
              variant={item.trending ? 'crimson' : item.trending === null ? 'banana' : 'smoke'}
              inverse
              className="p-1-i"
              onClick={() => {
                if (onUpdateMcm) {
                  onUpdateMcm(
                    toggleMcmEndpoint('trending', item.key, mcmEndpoints, sourceMcm, parentMcm)
                  );
                }
              }}
              icon="fire"
            />
          </div>
        );
      },
    },
  ];

  return (
    <fieldset disabled={props.readOnly}>
      {!isTemplate && (
        <BubbleCheckbox
          className="mb-3"
          checked={sourceMcm?.syncWithTemplate ?? true}
          onValue={() => {
            if (onUpdateMcm) {
              onUpdateMcm(toggleMcmSync(sourceMcm));
            }
          }}
          label="Default to Library"
        />
      )}
      <BubbleTable
        slim
        responsive
        hideHeader
        striped
        bordered
        headVariant="navy"
        stickyHeader={0}
        items={visibilityTableItems}
        templates={visibilityTableTemplates}
        fields={visibilityTableFields}
      />
    </fieldset>
  );
}
